import { useOutletContext } from 'react-router-dom'

import { Section } from '@factorialco/deprecated-design-system'
import { ProjectDetailsPageSettingsPageBudgetSectionFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ProjectBudgetForm, {
  IProjectBudgetValues,
} from 'modules/project_management/components/forms/ProjectBudgetForm'
import {
  useCreateBudgetStrategy,
  useDeleteBudgetStrategy,
  useUpdateBudgetStrategy,
} from 'modules/project_management/hooks/planningMutations/budgetStrategyHooks'
import { useCreateFixedCostPlanning } from 'modules/project_management/hooks/planningMutations/useCreateFixedCostPlanning'
import { useCreateFixedTimeBudget } from 'modules/project_management/hooks/planningMutations/useCreateFixedTimeBudget'
import { useDeleteFixedCostPlanning } from 'modules/project_management/hooks/planningMutations/useDeleteFixedCostPlanning'
import { useDeleteFixedTimeBudget } from 'modules/project_management/hooks/planningMutations/useDeleteFixedTimeBudget'
import { useUpdateFixedCostPlanning } from 'modules/project_management/hooks/planningMutations/useUpdateFixedCostPlanning'
import { useUpdateFixedTimeBudget } from 'modules/project_management/hooks/planningMutations/useUpdateFixedTimeBudget'
import { budgetTypesEnum } from 'modules/project_management/lib/constants'
import {
  getDecimalHoursFromMinutes,
  getMinutesFromDecimalHours,
} from 'modules/project_management/lib/formatHours'
import { isValidBudgetType } from 'modules/project_management/lib/utils'

export const FixedBudgetSection: React.FC<{
  project: ProjectDetailsPageSettingsPageBudgetSectionFragment
  hasTotalProfitabilityFeature: boolean
}> = ({ project, hasTotalProfitabilityFeature }) => {
  const { company } = useCurrentSession()

  const { create: createFixedTimeBudget } = useCreateFixedTimeBudget(company.id)
  const { update: updateFixedTimeBudget } = useUpdateFixedTimeBudget()
  const { delete: deleteFixedTimeBudget } = useDeleteFixedTimeBudget()
  const { create: createFixedCostPlanning } = useCreateFixedCostPlanning(company.id)
  const { update: updateFixedCostPlanning } = useUpdateFixedCostPlanning()
  const { delete: deleteFixedCostPlanning } = useDeleteFixedCostPlanning()
  const { createBudgetStrategy } = useCreateBudgetStrategy(company.id)
  const { updateBudgetStrategy } = useUpdateBudgetStrategy()
  const { deleteBudgetStrategy } = useDeleteBudgetStrategy()

  // Helper to get current budget type
  const getCurrentBudgetType = (): budgetTypesEnum => {
    if (project.fixedTimeBudget) return budgetTypesEnum.fixedTimeBudget
    if (project.fixedCostPlanning) return budgetTypesEnum.fixedCostPlanning
    if (project.budgetStrategy && isValidBudgetType(project.budgetStrategy.budgetType)) {
      return project.budgetStrategy.budgetType
    }
    return budgetTypesEnum.withoutBudget
  }

  // Different options for mutation
  const getMutation = (data: IProjectBudgetValues): Promise<UnhandledResult<Result<unknown>>> => {
    const currentBudgetType = getCurrentBudgetType()
    const targetBudgetType = data.budgetType

    // Handle deletion (withoutBudget)
    if (targetBudgetType === budgetTypesEnum.withoutBudget) {
      switch (currentBudgetType) {
        case budgetTypesEnum.fixedTimeBudget:
          if (!project.fixedTimeBudget) throw new Error('No total hours budget to delete')
          return deleteFixedTimeBudget({ id: project.fixedTimeBudget.id })
        case budgetTypesEnum.fixedCostPlanning:
          if (!project.fixedCostPlanning) throw new Error('No fixed price budget to delete')
          return deleteFixedCostPlanning({ id: project.fixedCostPlanning.id })
        case budgetTypesEnum.projectFixedCost:
          if (!project.budgetStrategy) {
            throw new Error('No fixed price budget to delete')
          }
          return deleteBudgetStrategy({ id: project.budgetStrategy.id })
        case budgetTypesEnum.totalBudget:
          if (!project.budgetStrategy) {
            throw new Error('No total budget to delete')
          }
          return deleteBudgetStrategy({ id: project.budgetStrategy.id })
        default:
          throw new Error('No budget to delete')
      }
    }

    // Handle updates (same type)
    if (currentBudgetType === targetBudgetType) {
      switch (targetBudgetType) {
        case budgetTypesEnum.fixedTimeBudget: {
          const fixedTimeBudget = project.fixedTimeBudget
          if (!fixedTimeBudget) throw new Error('No total hours budget to update')
          return updateFixedTimeBudget({
            id: fixedTimeBudget.id,
            budgetAllocation: getMinutesFromDecimalHours(Number(data.budgetAllocation)),
          })
        }
        case budgetTypesEnum.fixedCostPlanning: {
          const fixedCostPlanning = project.fixedCostPlanning
          if (!fixedCostPlanning) throw new Error('No fixed price budget to update')
          return updateFixedCostPlanning({
            fixedCostPlanningId: fixedCostPlanning.id,
            budgetAllocationCents: Math.round(Number(data.budgetAllocationCents) * 100),
            feeAmountCents: data.feeAmountCents
              ? Math.round(Number(data.feeAmountCents) * 100)
              : undefined,
          })
        }
        case budgetTypesEnum.projectFixedCost: {
          const budgetStrategy = project.budgetStrategy
          if (!budgetStrategy) {
            throw new Error('No project fixed cost to update')
          }
          return updateBudgetStrategy({
            id: budgetStrategy.id,
            budgetAllocation: data.plannedCents
              ? 1
              : Math.round(Number(data.budgetAllocation) * 100),
            budgetType: budgetTypesEnum.projectFixedCost,
            plannedCents:
              Number(data.plannedCents) === 0
                ? undefined
                : Math.round(Number(data.plannedCents) * 100),
            feeAmountCents: data.feeAmountCents
              ? Math.round(Number(data.feeAmountCents) * 100)
              : undefined,
          })
        }
        case budgetTypesEnum.totalBudget: {
          const budgetStrategy = project.budgetStrategy
          if (!budgetStrategy) {
            throw new Error('No total budget to update')
          }
          return updateBudgetStrategy({
            id: budgetStrategy.id,
            budgetAllocation: data.plannedCents
              ? 1
              : Math.round(Number(data.budgetAllocation) * 100),
            budgetType: budgetTypesEnum.totalBudget,
            plannedCents:
              Number(data.plannedCents) === 0
                ? undefined
                : Math.round(Number(data.plannedCents) * 100),
            plannedMinutes:
              Number(data.plannedMinutes) === 0
                ? undefined
                : Math.round(Number(data.plannedMinutes) * 60),
          })
        }
        default:
          throw new Error('Cannot update No budget option')
      }
    }

    // Handle creation/switching between types
    switch (targetBudgetType) {
      case budgetTypesEnum.fixedTimeBudget:
        return createFixedTimeBudget({
          projectId: project.id,
          budgetAllocation: getMinutesFromDecimalHours(Number(data.budgetAllocation)),
        })
      case budgetTypesEnum.fixedCostPlanning:
        return createFixedCostPlanning({
          projectId: project.id,
          budgetAllocationCents: Math.round(Number(data.budgetAllocationCents) * 100),
          feeAmountCents: data.feeAmountCents
            ? Math.round(Number(data.feeAmountCents) * 100)
            : undefined,
        })
      case budgetTypesEnum.projectFixedCost:
        return createBudgetStrategy({
          projectId: project.id,
          budgetType: budgetTypesEnum.projectFixedCost,
          budgetAllocation: data.budgetAllocation
            ? Math.round(Number(data.budgetAllocation) * 100)
            : 1,
          plannedCents:
            Number(data.plannedCents) === 0
              ? undefined
              : Math.round(Number(data.plannedCents) * 100),
          feeAmountCents: data.feeAmountCents
            ? Math.round(Number(data.feeAmountCents) * 100)
            : undefined,
        })
      case budgetTypesEnum.totalBudget:
        return createBudgetStrategy({
          projectId: project.id,
          budgetType: budgetTypesEnum.totalBudget,
          budgetAllocation: data.budgetAllocation
            ? Math.round(Number(data.budgetAllocation) * 100)
            : 1,
          plannedCents:
            Number(data.plannedCents) === 0
              ? undefined
              : Math.round(Number(data.plannedCents) * 100),
          plannedMinutes:
            Number(data.plannedMinutes) === 0
              ? undefined
              : Math.round(Number(data.plannedMinutes) * 60),
        })
      default:
        throw new Error(`Unknown budget type: ${targetBudgetType}`)
    }
  }

  const { hasCostsFeature } = useOutletContext<{
    hasCostsFeature: boolean
  }>()

  const getProjectBudgetDefaultValues = () => {
    const currentBudgetType = getCurrentBudgetType()
    const baseCurrency = { currencyType: project.legalEntity.currency }

    switch (currentBudgetType) {
      case budgetTypesEnum.fixedTimeBudget: {
        const fixedTimeBudget = project.fixedTimeBudget
        if (!fixedTimeBudget) throw new Error('No total hours budget found')
        return {
          budgetType: budgetTypesEnum.fixedTimeBudget,
          budgetAllocation: getDecimalHoursFromMinutes(fixedTimeBudget.budgetAllocation),
          budgetAllocationCents: null,
          feeAmountCents: null,
          plannedCents: null,
          plannedMinutes: null,
          ...baseCurrency,
        }
      }
      case budgetTypesEnum.fixedCostPlanning: {
        const fixedCostPlanning = project.fixedCostPlanning
        if (!fixedCostPlanning) throw new Error('No fixed price budget found')
        return {
          budgetType: budgetTypesEnum.fixedCostPlanning,
          budgetAllocation: null,
          budgetAllocationCents: fixedCostPlanning.budgetAllocationCents / 100,
          feeAmountCents: fixedCostPlanning.feeAmountCents
            ? fixedCostPlanning.feeAmountCents / 100
            : undefined,
          plannedCents: null,
          plannedMinutes: null,
          ...baseCurrency,
        }
      }
      case budgetTypesEnum.projectFixedCost: {
        const budgetStrategy = project.budgetStrategy
        if (!budgetStrategy) throw new Error('No fixed price budget found')
        return {
          budgetType: budgetTypesEnum.projectFixedCost,
          budgetAllocation: null,
          budgetAllocationCents: null,
          feeAmountCents: budgetStrategy.feeAmountCents
            ? budgetStrategy.feeAmountCents / 100
            : undefined,
          plannedCents: budgetStrategy.plannedCents ? budgetStrategy.plannedCents / 100 : undefined,
          plannedMinutes: null,
          ...baseCurrency,
        }
      }
      case budgetTypesEnum.totalBudget: {
        const budgetStrategy = project.budgetStrategy
        if (!budgetStrategy) {
          throw new Error('No total budget found')
        }
        return {
          budgetType: budgetTypesEnum.totalBudget,
          budgetAllocation: null,
          budgetAllocationCents: null,
          feeAmountCents: null,
          plannedCents: budgetStrategy.plannedCents ? budgetStrategy.plannedCents / 100 : undefined,
          plannedMinutes: budgetStrategy.plannedMinutes
            ? budgetStrategy.plannedMinutes / 60
            : undefined,
          ...baseCurrency,
        }
      }
      default:
        return {
          budgetType: budgetTypesEnum.withoutBudget,
          budgetAllocation: undefined,
          budgetAllocationCents: undefined,
          feeAmountCents: undefined,
          plannedCents: undefined,
          plannedMinutes: undefined,
          ...baseCurrency,
        }
    }
  }

  const { values, errors, setValues, setErrors, handleSubmit, dirty } =
    useForm<IProjectBudgetValues>({
      defaultValues: getProjectBudgetDefaultValues(),
      enableReinitialize: true,
      validateOnChange: true,
      validateOnBlur: true,
      validate: (data) => {
        const errorValidation: FormErrors<IProjectBudgetValues> = {}

        if (
          data.budgetType === budgetTypesEnum.fixedTimeBudget &&
          (!data.budgetAllocation || data.budgetAllocation <= 0)
        )
          errorValidation.budgetAllocation = i18n.t(
            'project_management.planning.planned_hours_error'
          )

        if (data.budgetType === budgetTypesEnum.fixedCostPlanning) {
          if (!data.budgetAllocationCents) {
            errorValidation.budgetAllocationCents = i18n.t(
              'project_management.planning.validation_errors.field_is_mandatory'
            )
          } else {
            if (data.budgetAllocationCents <= 0) {
              errorValidation.budgetAllocationCents = i18n.t(
                'project_management.planning.validation_errors.field_higher_than_0'
              )
            }
            if (isNaN(data.budgetAllocationCents)) {
              errorValidation.budgetAllocationCents = i18n.t(
                'project_management.planning.validation_errors.field_is_number'
              )
            }
          }
        }
        if (data.budgetType === budgetTypesEnum.projectFixedCost) {
          if (!data.plannedCents) {
            errorValidation.plannedCents = i18n.t(
              'project_management.planning.validation_errors.field_is_mandatory'
            )
          } else {
            if (data.plannedCents <= 0) {
              errorValidation.plannedCents = i18n.t(
                'project_management.planning.validation_errors.field_higher_than_0'
              )
            }
            if (isNaN(data.plannedCents)) {
              errorValidation.plannedCents = i18n.t(
                'project_management.planning.validation_errors.field_is_number'
              )
            }
          }
        }
        if (data.budgetType === budgetTypesEnum.fixedCostPlanning && data.feeAmountCents) {
          if (data.feeAmountCents <= 0) {
            errorValidation.feeAmountCents = i18n.t(
              'project_management.planning.validation_errors.field_higher_than_0'
            )
          }
          if (isNaN(data.feeAmountCents)) {
            errorValidation.feeAmountCents = i18n.t(
              'project_management.planning.validation_errors.field_is_number'
            )
          }
        }

        if (data.budgetType === budgetTypesEnum.totalBudget) {
          if (data.plannedCents !== undefined && data.plannedCents !== null) {
            if (isNaN(data.plannedCents)) {
              errorValidation.plannedCents = i18n.t(
                'project_management.forms.financial_details.fixed_costs.format_error'
              )
            } else if (data.plannedCents < 0) {
              errorValidation.plannedCents = i18n.t(
                'project_management.forms.financial_details.fixed_costs.negative_error'
              )
            }
          }
        }

        return errorValidation
      },
      onSubmit: async (data) => {
        const result = await getMutation(data)

        const handled = handleResult(result, (err) => {
          if (err) setErrors(err)
        })

        if (handled.success) {
          const trackBudgetAction = () => {
            const currentBudgetType = getCurrentBudgetType()
            const targetBudgetType = data.budgetType

            // Handle deletion case
            if (targetBudgetType === budgetTypesEnum.withoutBudget) {
              if (currentBudgetType !== budgetTypesEnum.withoutBudget) {
                track(trackingEvents.projectManagement.settings.deleteBudget)
              }
              return
            }

            // Get tracking budget type name
            const getTrackingBudgetType = (budgetType: string) => {
              switch (budgetType) {
                case budgetTypesEnum.fixedTimeBudget:
                  return 'fixed time budget'
                case budgetTypesEnum.fixedCostPlanning:
                  return project.isBillable ? 'Planned_cost_income' : 'Planned_cost'
                case budgetTypesEnum.projectFixedCost:
                  return 'project fixed cost'
                case budgetTypesEnum.totalBudget:
                  return 'total budget'
                default:
                  return budgetType
              }
            }

            const trackingBudgetType = getTrackingBudgetType(targetBudgetType)
            const isUpdate = currentBudgetType === targetBudgetType
            const isCreate = currentBudgetType === budgetTypesEnum.withoutBudget

            // Determine tracking parameters
            const trackingParams: Record<string, string> = {
              budgetType: trackingBudgetType,
            }

            // Add specific parameters for fixed cost planning
            if (
              targetBudgetType === budgetTypesEnum.fixedCostPlanning ||
              targetBudgetType === budgetTypesEnum.projectFixedCost
            ) {
              trackingParams.income = data.feeAmountCents ? 'yes' : 'no'

              // Add amountChanged for updates
              if (isUpdate && project.fixedCostPlanning) {
                trackingParams.amountChanged =
                  data.budgetAllocationCents !==
                  project.fixedCostPlanning.budgetAllocationCents / 100
                    ? 'yes'
                    : 'no'
              } else if (!isCreate) {
                trackingParams.amountChanged = 'yes'
              }
            } else if (isUpdate && !isCreate) {
              // For other budget types, always mark amount as changed when updating
              trackingParams.amountChanged = 'yes'
            }

            // Track the appropriate event
            if (isCreate) {
              track(trackingEvents.projectManagement.settings.addBudget, trackingParams)
            } else {
              track(trackingEvents.projectManagement.settings.updateBudget, trackingParams)
            }
          }

          trackBudgetAction()

          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
        }

        return handled
      },
    })

  const handleFieldChanged = (
    fieldName: string,
    fieldValue: number | string | undefined,
    shouldValidate = false
  ) => {
    const newValues = { ...values, [fieldName]: fieldValue }

    if (fieldName === 'budgetType') {
      newValues['budgetAllocation'] = undefined
      newValues['budgetAllocationCents'] = undefined
      newValues['feeAmountCents'] = undefined
      newValues['plannedCents'] = undefined
      newValues['plannedMinutes'] = undefined

      setErrors({})
    }

    setValues(newValues, shouldValidate)
  }

  return (
    <Section.Form
      illustration="money"
      title={i18n.t('project_management.planning.title')}
      description={i18n.t('project_management.planning.description')}
      onSubmit={handleSubmit}
      submitLabel={i18n.t('common.submit')}
      isDirty={dirty}
      paddingX="s0"
    >
      <ProjectBudgetForm
        onChangeValue={handleFieldChanged}
        values={values}
        errors={errors}
        isBillable={project.isBillable ?? false}
        isOwner={!!project._policies?.assignOwner.can}
        hasCostsFeature={hasCostsFeature}
        hasBudgetFeature
        hasTotalProfitabilityFeature={hasTotalProfitabilityFeature}
      />
    </Section.Form>
  )
}
